import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  getIsFetching,
  getError,
  getSubscriptionStatus,
} from 'src/effects/SubscriptionStatus/selectors'
import { validateEmail } from 'client/helpers/Form'
import { subscribeRequestAction } from 'src/effects/SubscriptionStatus/actions'

import ButtonSimple from 'src/comps/ButtonSimple'
import Feedback from './Feedback'
import FormErrorMessage from 'client/components/FormErrorMessage'
import IconEmail from 'client/icons/Email'
import IconWarning from 'client/icons/Warning'
import InputLine from 'client/components/InputLine'

import messages from './messages'

import {
  ButtonContainer,
  InputSection,
  SubscriptionsBase,
  SubTitle,
  SubscriptionBox,
  Title,
  WrapperInput,
} from './styles'
import { SUBSCRIBED, PENDING } from 'src/effects/SubscriptionStatus/constants'
import { useMarket } from 'src/hooks/useMarket'

const Subscriptions = ({ modalVariation, source, withTitles }) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { language, feedbackMessageType, market } = useMarket()

  const subscriptionError = useSelector(getError)
  const isFetching = useSelector(getIsFetching)
  const subscriptionStatus = useSelector(getSubscriptionStatus)

  const [email, setEmail] = useState('')
  const [subscriptionSource, setSubscriptionSource] = useState('')
  const [errors, setErrors] = useState({ email: false })
  const [formSubmit, setFormSubmit] = useState(false)

  let feedbackMessage = intl.formatMessage(messages.willReceiveEmail)

  if (subscriptionStatus !== PENDING || feedbackMessageType === 'text') {
    feedbackMessage = intl.formatMessage(messages.thankYouSubscription)
  }

  useEffect(() => {
    setSubscriptionSource(source)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function validForm() {
    const error = {
      email: validateEmail(email),
    }

    setErrors(error)
    return !error.email
  }

  function handleSubmitForm(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!validForm()) {
      return
    }

    setFormSubmit(true)

    dispatch(
      subscribeRequestAction({
        hash: email,
        market,
        language,
        feedbackMessageType,
        source: subscriptionSource,
      })
    )
  }

  function isSubscribed(status) {
    return status === SUBSCRIBED || status === PENDING
  }

  return (
    <>
      {!formSubmit && (
        <SubscriptionsBase data-testid="subscriptions-view" withTitles={withTitles}>
          {(withTitles || modalVariation) && (
            <>
              <Title modalVariation={modalVariation}>
                <FormattedMessage {...messages.subscribeTitle} />
              </Title>
              <SubTitle>
                <FormattedMessage {...messages.subscribeSubTitle} />
              </SubTitle>
            </>
          )}

          <SubscriptionBox>
            <WrapperInput modalVariation={modalVariation}>
              <InputSection modalVariation={modalVariation}>
                <InputLine
                  label={intl.formatMessage(messages.email)}
                  name="email"
                  onChange={event => setEmail(event.target.value)}
                  type="email"
                  value={email}
                  role="textbox"
                  icon={<IconEmail />}
                />
              </InputSection>
              {errors.email && <FormErrorMessage errors={errors.email} />}
            </WrapperInput>

            <ButtonContainer modalVariation={modalVariation}>
              <ButtonSimple
                dataTestId="subscribeform-submit"
                fullWidth
                isLoading={isFetching}
                onClick={event => handleSubmitForm(event)}
                text={intl.formatMessage(messages.subscribeCTA)}
                variant="primary"
              />
            </ButtonContainer>
          </SubscriptionBox>
        </SubscriptionsBase>
      )}

      {isSubscribed(subscriptionStatus) && formSubmit && (
        <Feedback
          id="pending-view"
          icon={<IconEmail />}
          title={intl.formatMessage(messages.thankYou)}
          subtitle={feedbackMessage}
          withBorderTop={false}
        />
      )}

      {subscriptionError && formSubmit && (
        <Feedback
          id="error-view"
          icon={<IconWarning />}
          title={intl.formatMessage(messages.error)}
          withBorderTop={false}
        />
      )}
    </>
  )
}

Subscriptions.propTypes = {
  modalVariation: PropTypes.bool,
  source: PropTypes.string.isRequired,
  withTitles: PropTypes.bool,
}

export default Subscriptions
